export type FilterChipOption<T extends string> = {
  value: T;
  label: string;
  count?: number;
};

export type FilterChipsProps<T extends string> = {
  options: FilterChipOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Names the group for screen readers, e.g. "Filter deliveries". */
  ariaLabel?: string;
  className?: string;
};

/**
 * One choice out of a few, so the chips are a radio group: `role="radio"` with
 * `aria-checked` (an `aria-pressed` toggle would not say which one is picked).
 * Every chip stays focusable, so Tab walks the group.
 */
const FilterChips = <T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
}: FilterChipsProps<T>) => (
  <div
    role="radiogroup"
    aria-label={ariaLabel}
    className={`flex flex-wrap items-center gap-2 ${className ?? ''}`}
  >
    {options.map((option) => {
      const isActive = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={isActive}
          onClick={() => onChange(option.value)}
          className={`flex h-8 items-center gap-2 rounded-sm border px-3 text-sm ${
            isActive
              ? 'border-black bg-muted-white font-semibold dark:border-white dark:bg-design-black-accent dark:text-white'
              : 'border-design-secondary-black text-light-black dark:text-sublte-white'
          }`}
        >
          <span>{option.label}</span>
          {option.count !== undefined && (
            <span className="text-xs">{option.count}</span>
          )}
        </button>
      );
    })}
  </div>
);

export default FilterChips;
